import type {AccountResponseDto} from '@resurs-internal/rb-app-api/api/deposits-openbanking-service';
import {
  useDepositOpenBankingAccounts,
  openbankingExchangeStateTerminalList,
  type OpenBankingExchangeStateTerminal,
} from '@resurs-internal/rb-app-api/features/deposit/hooks/useDepositOpenBankingAccounts/useDepositOpenBankingAccounts';
import {useQuery} from '@tanstack/react-query';
import {useState} from 'react';
import {z} from 'zod';
import {getObDisbursementSession} from '../utils/disbursementSessionStorage';

const exchangeStateSchema = z.enum(openbankingExchangeStateTerminalList);
export const ExchangeState = exchangeStateSchema.enum;

type DisbursementExchangeData = {
  state: OpenBankingExchangeStateTerminal;
  accounts: AccountResponseDto[];
};

export const DISBURSEMENT_EXCHANGE_QUERY_KEY = [
  'deposit',
  'openbanking',
  'disbursement-exchange',
];

export const useDisbursementExchange = () => {
  const {exchange, accounts: accountsMutation} =
    useDepositOpenBankingAccounts();
  const [session] = useState(getObDisbursementSession);

  return useQuery<DisbursementExchangeData>({
    // Keyed on the session so a result restored from a previous session (e.g.
    // persisted before `persist: false` was added) can't satisfy this one. The
    // ids are not sent from here; queryFn builds the request from `session`.
    queryKey: [
      ...DISBURSEMENT_EXCHANGE_QUERY_KEY,
      session?.applicantId,
      session?.sessionId,
    ],
    queryFn: async (): Promise<DisbursementExchangeData> => {
      if (!session) {
        return {state: ExchangeState.FAILED, accounts: []};
      }

      const exchangeResponse = await exchange.mutateAsync({
        applicantId: session.applicantId,
        sessionId: session.sessionId,
      });

      const parsedState = exchangeStateSchema.safeParse(exchangeResponse.state);

      if (!parsedState.success) {
        return {state: ExchangeState.UNKNOWN, accounts: []};
      }

      if (parsedState.data !== ExchangeState.SUCCEEDED) {
        return {state: parsedState.data, accounts: []};
      }

      if (!exchangeResponse.tokenDetails?.accessToken) {
        return {state: ExchangeState.FAILED, accounts: []};
      }

      const accountsResponse = await accountsMutation.mutateAsync(
        exchangeResponse.tokenDetails.accessToken,
      );

      // Filter out accounts without a BBAN account number — those cannot be
      // used as a disbursement target. We intentionally do NOT filter on
      // `capabilities.withdrawFunds` here; many real banks return accounts
      // as `type: UNKNOWN` (and therefore `withdrawFunds: false`), but they
      // are perfectly valid disbursement targets as long as we have the BBAN.
      const eligible =
        accountsResponse.accounts?.filter(
          (a) => !!a.fundingDetails?.bban?.accountNumber,
        ) ?? [];

      return {state: ExchangeState.SUCCEEDED, accounts: eligible};
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // The query cache is persisted to localStorage and restored on reload, so
    // without this the previous exchange (including the customer's bank
    // accounts) comes back after returning from the bank.
    meta: {persist: false},
  });
};
